/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, Video, VideoOff, PhoneOff, StickyNote, Pill, FileAudio } from 'lucide-react';
import { cn } from '@/lib/utils';

const ROUND_BTN = 'h-12 w-12 rounded-full flex items-center justify-center transition-colors';
const IDLE = 'bg-white/10 hover:bg-white/20 text-white';
const ACTIVE = 'bg-primary text-primary-foreground';

/**
 * VideoControlsBar — FaceTime-style bottom bar for the video visit.
 * Extracted from VideoRoomPage.jsx (Code Quality refactor).
 */
export const VideoControlsBar = ({
  micOn, camOn, onToggleMic, onToggleCam, onHangUp, endLoading,
  isDoctor, rxOpen, onOpenRx, transcriptOpen, onToggleTranscript, notesOpen, onToggleNotes,
}) => {
  const { t } = useTranslation();
  return (
    <div className="relative flex-shrink-0 bg-black/70 backdrop-blur-md border-t border-white/10 p-4 flex items-center justify-center gap-3 z-20">
      <button
        onClick={onToggleMic}
        className={cn(ROUND_BTN, micOn ? IDLE : 'bg-emergency text-white')}
        data-testid="video-mic-toggle"
        aria-pressed={!micOn}
        aria-label={micOn ? 'Disattiva microfono' : 'Attiva microfono'}
      >
        {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
      </button>
      <button
        onClick={onToggleCam}
        className={cn(ROUND_BTN, camOn ? IDLE : 'bg-emergency text-white')}
        data-testid="video-cam-toggle"
        aria-pressed={!camOn}
        aria-label={camOn ? 'Disattiva fotocamera' : 'Attiva fotocamera'}
      >
        {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
      </button>
      <button
        onClick={onHangUp}
        className={cn('h-14 w-14 rounded-full flex items-center justify-center transition-colors bg-emergency hover:bg-emergency/90 text-white', endLoading && 'opacity-70 pointer-events-none')}
        data-testid="video-hangup-btn"
        aria-label="Termina chiamata"
      >
        <PhoneOff className="h-6 w-6" />
      </button>
      {isDoctor && (
        <>
          <button
            onClick={onOpenRx}
            className={cn(ROUND_BTN, rxOpen ? ACTIVE : IDLE)}
            data-testid="video-rx-toggle"
            title={t('rxTitle')}
            aria-label={t('rxTitle')}
          >
            <Pill className="h-5 w-5" />
          </button>
          <button
            onClick={onToggleTranscript}
            className={cn(ROUND_BTN, transcriptOpen ? ACTIVE : IDLE)}
            data-testid="video-transcript-toggle"
            title="Trascrizione AI"
            aria-label="Trascrizione AI"
            aria-pressed={transcriptOpen}
          >
            <FileAudio className="h-5 w-5" />
          </button>
          <button
            onClick={onToggleNotes}
            className={cn(ROUND_BTN, notesOpen ? ACTIVE : IDLE)}
            data-testid="video-notes-toggle"
            aria-label="Note cliniche"
            aria-pressed={notesOpen}
          >
            <StickyNote className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
};
